import React, { useState } from 'react';
import { Tooltip } from 'components/tool-tip/tooltip';
import { PersonalityCoreArea, PersonalityEmployerExpectation } from 'hooks/usePersonality';

export type AlignmentTier = 'excellent' | 'good' | 'moderate' | 'different';

interface TierStyle {
  matchLabel: string;
  summaryLabel: string;
  text: string;
  background: string;
  border: string;
  bar: string;
}

export const ALIGNMENT_TIERS: Record<AlignmentTier, TierStyle> = {
  excellent: {
    matchLabel: 'Excellent match',
    summaryLabel: 'Excellent Alignment',
    text: '#069952',
    background: 'rgba(230, 255, 243, 0.7)',
    border: '#069952',
    bar: '#069952',
  },
  good: {
    matchLabel: 'Good match',
    summaryLabel: 'Good Alignment',
    text: '#069952',
    background: 'rgba(230, 255, 243, 0.7)',
    border: '#069952',
    bar: '#069952',
  },
  moderate: {
    matchLabel: 'Moderate match',
    summaryLabel: 'Moderate Alignment',
    text: '#DB8400',
    background: '#FFF5E6',
    border: '#DB8400',
    bar: '#DB8400',
  },
  different: {
    matchLabel: 'Different style',
    summaryLabel: 'Low Alignment',
    text: '#344054',
    background: '#F2F4F7',
    border: '#344054',
    bar: '#E62E2E',
  },
};

export const getAlignmentTier = (alignment: number): AlignmentTier => {
  if (alignment >= 80) return 'excellent';
  if (alignment >= 60) return 'good';
  if (alignment >= 40) return 'moderate';
  return 'different';
};

export interface PersonalityDimension {
  title: string;
  /** Trait at the far left of the scale, e.g. "Outcome-driven". */
  leftLabel: string;
  /** Trait at the far right of the scale, e.g. "Autonomous". */
  rightLabel: string;
  /** How strongly the candidate leans to each pole, 0-100 measured out from Neutral. */
  leftScore: number;
  rightScore: number;
  /** Where the employer's preference sits, and which pole it belongs to. */
  employerScore: number;
  employerSide: 'left' | 'right';
  /** 0-100, how closely the candidate meets that preference. */
  alignment: number;
}

const clampScore = (score?: number): number => {
  if (typeof score !== 'number' || !Number.isFinite(score)) return 0;
  return Math.min(100, Math.max(0, score));
};

const titleCase = (value?: string): string =>
  (value || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalise = (value?: string): string =>
  (value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const isSameBoundary = (a?: string, b?: string): boolean => {
  const left = normalise(a);
  const right = normalise(b);
  return !!left && !!right && left === right;
};

/**
 * Boundary names are not written the same way everywhere — an expectation may be
 * recorded as "Autonomous" while the boundary reads "Independent Introvert
 * (Autonomous)" — so fall back to either name containing the other.
 */
const isRelatedBoundary = (a?: string, b?: string): boolean => {
  const left = normalise(a);
  const right = normalise(b);
  if (!left || !right) return false;
  return left === right || left.includes(right) || right.includes(left);
};

/**
 * Which pole did the employer ask for? `employer_expectations[].name` is meant to say,
 * but it does not reliably match either boundary name, and a partial match can hit both
 * poles ("Independent Introvert" vs "Independent Extrovert"). Where the name is missing
 * or ambiguous, `candidate_score` settles it: it is the candidate's score on the
 * preferred pole, so whichever boundary it equals is the one being matched against.
 */
const resolveEmployerSide = (
  area: PersonalityCoreArea,
  expectation: PersonalityEmployerExpectation | undefined,
  leftScore: number,
  rightScore: number
): 'left' | 'right' => {
  const bottomName = area?.bottom_boundary?.name;
  const topName = area?.top_boundary?.name;

  if (isSameBoundary(expectation?.name, bottomName)) return 'left';
  if (isSameBoundary(expectation?.name, topName)) return 'right';

  const relatedToBottom = isRelatedBoundary(expectation?.name, bottomName);
  const relatedToTop = isRelatedBoundary(expectation?.name, topName);
  if (relatedToBottom !== relatedToTop) return relatedToBottom ? 'left' : 'right';

  const candidateScore = expectation?.candidate_score;
  if (typeof candidateScore === 'number' && Number.isFinite(candidateScore)) {
    const matchesLeft = Math.abs(clampScore(candidateScore) - leftScore) < 0.5;
    const matchesRight = Math.abs(clampScore(candidateScore) - rightScore) < 0.5;
    if (matchesLeft !== matchesRight) return matchesLeft ? 'left' : 'right';
  }

  return 'right';
};

/**
 * Each core area is a bipolar scale: the candidate scores on both poles, while the
 * employer picks one of them as their preference. That preferred pole is the half of
 * the scale being matched against — the other half is drawn as context only.
 */
export const buildPersonalityDimensions = (
  coreAreas?: PersonalityCoreArea[],
  employerExpectations?: PersonalityEmployerExpectation[]
): PersonalityDimension[] => {
  if (!Array.isArray(coreAreas)) return [];

  return coreAreas
    .filter((area) => area?.top_boundary || area?.bottom_boundary)
    .map((area) => {
      const expectation =
        employerExpectations?.find((exp) => isSameBoundary(exp?.title, area?.title)) ??
        employerExpectations?.find((exp) => isRelatedBoundary(exp?.title, area?.title));

      const leftScore = clampScore(area.bottom_boundary?.score);
      const rightScore = clampScore(area.top_boundary?.score);
      const employerSide = resolveEmployerSide(area, expectation, leftScore, rightScore);
      const employerScore = clampScore(expectation?.score);
      const candidateScore = clampScore(
        expectation?.candidate_score ?? (employerSide === 'left' ? leftScore : rightScore)
      );

      return {
        title: titleCase(area.title),
        leftLabel: area.bottom_boundary?.name || '',
        rightLabel: area.top_boundary?.name || '',
        leftScore,
        rightScore,
        employerScore,
        employerSide,
        alignment: Math.max(0, 100 - Math.abs(employerScore - candidateScore)),
      };
    });
};

/** Distance from Neutral (50%) to a pole is half the track, so a 0-100 score maps to 0-50%. */
const toOffset = (score: number): number => score / 2;

const Dot: React.FC<{ offsetPercent: number; variant: 'employer' | 'candidate' }> = ({
  offsetPercent,
  variant,
}) => (
  <span
    className="absolute top-1/2 z-20 block h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
    style={{
      left: `${offsetPercent}%`,
      background: variant === 'employer' ? '#012169' : '#FFFFFF',
      border: variant === 'candidate' ? '1.5px solid #069952' : undefined,
    }}
  />
);

interface AlignmentRowProps {
  dimension: PersonalityDimension;
  showEmployer: boolean;
  showCandidate: boolean;
}

const AlignmentRow: React.FC<AlignmentRowProps> = ({ dimension, showEmployer, showCandidate }) => {
  const { leftLabel, rightLabel, leftScore, rightScore, employerScore, employerSide } = dimension;
  const tier = ALIGNMENT_TIERS[getAlignmentTier(dimension.alignment)];

  const preferredScore = employerSide === 'left' ? leftScore : rightScore;
  const oppositeScore = employerSide === 'left' ? rightScore : leftScore;

  // The bars are candidate magnitudes; only their colour comes from the employer. With the
  // employer series hidden there is no match to state, so the badge goes and the bars go grey.
  const showMatch = showEmployer && showCandidate;

  // Read the exact numbers off the track: the bars are only 8px tall, so the whole row is
  // the hover target rather than each segment.
  const readout = (
    <div className="space-y-2">
      <p className="font-semibold text-[#1D2939]">{dimension.title}</p>
      {showEmployer && (
        <div>
          <p className="font-medium">Employer Preference</p>
          <p>
            {employerSide === 'left' ? leftLabel : rightLabel} · {Math.round(employerScore)}%
          </p>
        </div>
      )}
      {showCandidate && (
        <div>
          <p className="font-medium">Candidate Position</p>
          <p>
            {leftLabel} · {Math.round(leftScore)}%
          </p>
          <p>
            {rightLabel} · {Math.round(rightScore)}%
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-[9px]">
      <div className="flex min-h-[26px] w-full items-end justify-between gap-3">
        <p className="min-w-0 truncate text-base font-medium text-[#1D2939] sm:text-lg" title={dimension.title}>
          {dimension.title}
        </p>
        {showMatch && (
          <span
            className="shrink-0 whitespace-nowrap rounded-2xl border px-3 py-[3px] text-[11px] sm:text-xs"
            style={{ background: tier.background, borderColor: tier.border, color: tier.text }}
          >
            {tier.matchLabel}
          </span>
        )}
      </div>

      <div className="flex w-full flex-col">
        <Tooltip
          className="w-full cursor-pointer"
          width={320}
          content={readout}
          disabled={!showEmployer && !showCandidate}
        >
          <div className="relative h-[18px] w-full">
            <div className="absolute left-0 right-0 top-[5px] h-2 rounded bg-[#EAECF0]" />

            {/* The pole the employer asked for, coloured by how close the candidate came. */}
            {showCandidate && preferredScore > 0 && (
              <div
                className="absolute top-[5px] h-2"
                style={{
                  background: showMatch ? tier.bar : '#98A2B3',
                  width: `${toOffset(preferredScore)}%`,
                  ...(employerSide === 'left'
                    ? { right: '50%', borderTopLeftRadius: 4, borderBottomLeftRadius: 4 }
                    : { left: '50%', borderTopRightRadius: 4, borderBottomRightRadius: 4 }),
                }}
              />
            )}

            {/* The opposite pole is shown for context, never scored. */}
            {showCandidate && oppositeScore > 0 && (
              <div
                className="absolute top-[5px] h-2 bg-[#98A2B3]"
                style={{
                  width: `${toOffset(oppositeScore)}%`,
                  ...(employerSide === 'left'
                    ? { left: '50%', borderTopRightRadius: 4, borderBottomRightRadius: 4 }
                    : { right: '50%', borderTopLeftRadius: 4, borderBottomLeftRadius: 4 }),
                }}
              />
            )}

            <span className="absolute left-1/2 top-0 z-10 h-full w-px -translate-x-1/2 rounded-full bg-[#475467]" />

            {showCandidate && leftScore > 0 && <Dot offsetPercent={50 - toOffset(leftScore)} variant="candidate" />}
            {showCandidate && rightScore > 0 && <Dot offsetPercent={50 + toOffset(rightScore)} variant="candidate" />}
            {showEmployer && (
              <Dot
                offsetPercent={employerSide === 'left' ? 50 - toOffset(employerScore) : 50 + toOffset(employerScore)}
                variant="employer"
              />
            )}
          </div>
        </Tooltip>

        <div className="relative flex w-full items-center justify-between text-xs leading-5 text-[#344054] sm:text-sm">
          <p className="max-w-[38%] truncate" title={leftLabel}>
            {leftLabel}
          </p>
          <p className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap">Neutral</p>
          <p className="max-w-[38%] truncate text-right" title={rightLabel}>
            {rightLabel}
          </p>
        </div>
      </div>
    </div>
  );
};

interface LegendKeyProps {
  label: string;
  variant: 'employer' | 'candidate';
  active: boolean;
  onToggle: () => void;
}

const LegendKey: React.FC<LegendKeyProps> = ({ label, variant, active, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-pressed={active}
    className="flex items-center gap-3 rounded transition-opacity hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#012169]"
  >
    <span
      className={`block h-3.5 w-3.5 shrink-0 rounded-full ${active ? '' : 'opacity-40'}`}
      style={{
        background: variant === 'employer' ? '#012169' : '#FFFFFF',
        border: variant === 'candidate' ? '1.5px solid #069952' : undefined,
      }}
    />
    <p
      className={`whitespace-nowrap text-sm font-medium sm:text-base ${active ? 'text-[#1D2939]' : 'text-[#98A2B3] line-through'
        }`}
    >
      {label}
    </p>
  </button>
);

export interface PersonalityAlignmentChartProps {
  coreAreas?: PersonalityCoreArea[];
  employerExpectations?: PersonalityEmployerExpectation[];
  className?: string;
}

const PersonalityAlignmentChart: React.FC<PersonalityAlignmentChartProps> = ({
  coreAreas,
  employerExpectations,
  className = '',
}) => {
  const [showEmployer, setShowEmployer] = useState(true);
  const [showCandidate, setShowCandidate] = useState(true);

  const dimensions = buildPersonalityDimensions(coreAreas, employerExpectations);

  if (!dimensions.length) return null;

  return (
    <div className={`w-full rounded-xl border border-[#D1E9FF] bg-white px-5 py-7 sm:px-8 md:px-12 md:py-8 ${className}`}>
      <div className="flex flex-wrap items-center gap-x-10 gap-y-3 sm:gap-x-12">
        <LegendKey
          label="Candidate Position"
          variant="candidate"
          active={showCandidate}
          onToggle={() => setShowCandidate((shown) => !shown)}
        />
        <LegendKey
          label="Employer Preference"
          variant="employer"
          active={showEmployer}
          onToggle={() => setShowEmployer((shown) => !shown)}
        />
      </div>

      <div className="mt-8 flex w-full flex-col gap-7">
        {dimensions.map((dimension, index) => (
          <AlignmentRow
            key={`${dimension.title}-${index}`}
            dimension={dimension}
            showEmployer={showEmployer}
            showCandidate={showCandidate}
          />
        ))}
      </div>
    </div>
  );
};

export interface PersonalityAlignmentBadgeProps {
  score?: number;
  className?: string;
}

/** The overall alignment pill that sits under the Personality score in the report header. */
const PersonalityAlignmentBadge: React.FC<PersonalityAlignmentBadgeProps> = ({ score, className = '' }) => {
  if (typeof score !== 'number' || !Number.isFinite(score)) return null;

  const tier = ALIGNMENT_TIERS[getAlignmentTier(score)];

  return (
    <span
      className={`flex w-fit items-center justify-center rounded-2xl px-2.5 py-1 text-sm font-medium sm:text-base ${className}`}
      style={{ background: tier.background, color: tier.text }}
    >
      {tier.summaryLabel}
    </span>
  );
};

export { PersonalityAlignmentChart, PersonalityAlignmentBadge };
